import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { apiTokenRequest } from './authConfig';
import { fetchMyMenuAccess } from './api/apiMetadataApi';
import Dashboard from './components/Dashboard';
import HistoricalDashboard from './components/HistoricalDashboard';
import LogsInsights from './components/LogsInsights';
import SourceInfo from './components/SourceInfo';
import StaffDetails from './components/StaffDetails';
import { AccessDenied, NotFound, PermissionsUnavailable } from './components/AccessStatus';
import './App.css';
import { NavLink, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import Dropdown from "./components/Dropdown";
import {User, LogOut} from 'lucide-react';

// Route and page for each dbo.Menu.MenuKey. The menus' names, order and active
// status come from the database (sp_GetMenus, returned by my-menu-access); a menu
// is shown only when it is active there and has a route here. To hide a menu, set
// dbo.Menu.IsActive = 0 rather than removing its route.
const MENU_ROUTES = {
  Home: { path: '/', render: (accessToken) => <Dashboard accessToken={accessToken} /> },
  HistoricalData: { path: '/historical-dashboard', render: (accessToken) => <HistoricalDashboard accessToken={accessToken} /> },
  LogsAndInsights: { path: '/logs-insights', render: (accessToken) => <LogsInsights accessToken={accessToken} /> },
  SourceInfo: { path: '/source-info', render: (accessToken) => <SourceInfo accessToken={accessToken} /> },
  StaffDetails: { path: '/staff-details', render: (accessToken) => <StaffDetails accessToken={accessToken} /> },
};
// Staff Details is granted by IsOwner only, never by an assigned menu permission.
const OWNER_MENU_KEY = 'StaffDetails';

function App() {
  const { instance, accounts, inProgress } = useMsal();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [accessToken, setAccessToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Active menus from dbo.Menu ({ id, menuKey, label }, in DisplayOrder) that have a route.
  const [navMenus, setNavMenus] = useState([]);
  // MenuKeys the signed-in user may see/open: every active menu (including Staff
  // Details) for an Owner; otherwise only their assigned menus (StaffMenuPermission,
  // never Staff Details). Empty until the check has succeeded.
  const [allowedMenuKeys, setAllowedMenuKeys] = useState([]);
  // true when the menu access check failed — no page is shown until a retry succeeds.
  const [menuAccessError, setMenuAccessError] = useState(false);
  // false until the first menu access check has finished (granted, denied or failed);
  // until then the loader is shown instead of any page or "no permission" message.
  const [menuAccessChecked, setMenuAccessChecked] = useState(false);
  // true when the backend returned 403: the Azure AD user is not registered (or not
  // active) in AzureAdMenuAccessPermission, so the dashboard is not shown at all.
  const [notRegistered, setNotRegistered] = useState(false);
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const navRef = useRef(null);

  useEffect(() => { const handleClickOutside = (event) => { if ( navRef.current && !navRef.current.contains(event.target) && !event.target.closest(".nav-toggle") ) { setIsOpen(false); } }; 
document.addEventListener("mousedown", handleClickOutside); return () => document.removeEventListener("mousedown", handleClickOutside); }, []);


  // Asks the backend for the active menus and the signed-in user's { isOwner,
  // menuKeys } (the backend identifies the user from the access token and does the
  // lookup). If the API call fails, no menu is granted: the nav is empty and every
  // page shows a "couldn't verify" message with Retry, rather than failing open.
  const evaluateNavAccess = useCallback(async () => {
    try {
      const access = await fetchMyMenuAccess();
      const menus = (Array.isArray(access?.menus) ? access.menus : []).filter((m) => MENU_ROUTES[m.menuKey]);
      const activeKeys = menus.map((m) => m.menuKey);
      setNavMenus(menus);
      setMenuAccessError(false);
      setNotRegistered(false);
      if (access?.isOwner) {
        setAllowedMenuKeys(activeKeys);
        return;
      }
      const granted = (access?.menuKeys || []).map((k) => String(k).toLowerCase());
      setAllowedMenuKeys(activeKeys.filter((k) => k !== OWNER_MENU_KEY && granted.includes(k.toLowerCase())));
    } catch (err) {
      setNavMenus([]);
      setAllowedMenuKeys([]);
      if (err?.response?.status === 403) {
        setNotRegistered(true);
        setMenuAccessError(false);
        return;
      }
      console.error('Menu access check failed; no menus granted:', err);
      setMenuAccessError(true);
    } finally {
      setMenuAccessChecked(true);
    }
  }, []);

  const canAccess = (menuKey) => allowedMenuKeys.includes(menuKey);
  // The first page the user may open (logo / Not Found target); null if none.
  const defaultPath = MENU_ROUTES[navMenus.find((m) => canAccess(m.menuKey))?.menuKey]?.path ?? null;
  // Every route goes through this, so typed URLs, refreshes, bookmarks and
  // Back/Forward are checked the same way as nav clicks. The page's component is
  // only rendered when access is granted.
  const guard = (menuKey, element) => {
    if (menuAccessError) return <PermissionsUnavailable onRetry={evaluateNavAccess} />;
    if (canAccess(menuKey)) return element;
    // "/" is Home's path; a user without Home is sent to the first page they may open.
    if (MENU_ROUTES[menuKey].path === '/' && defaultPath) return <Navigate to={defaultPath} replace />;
    return <AccessDenied />;
  };

  const acquireAccessToken = useCallback(async () => {
    try {
      const response = await instance.acquireTokenSilent({
        ...apiTokenRequest,
        account: accounts[0],
      });
      setAccessToken(response.accessToken);
      setError(null);
      await evaluateNavAccess();
      setLoading(false);
    } catch (err) {
      if (err.errorCode === 'interaction_required' || err.errorCode === 'consent_required') {
        try {
          const response = await instance.acquireTokenPopup({
            ...apiTokenRequest,
            account: accounts[0],
          });
          setAccessToken(response.accessToken);
          setError(null);
          await evaluateNavAccess();
          setLoading(false);
          return;
        } catch (popupErr) {
          console.error('Popup token acquisition failed:', popupErr);
        }
      }
      setError(`Failed to acquire API token. Error code: ${err.errorCode}`);
      setLoading(false);
    }
  }, [instance, accounts, evaluateNavAccess]);

  const handleLogin = useCallback(async () => {
    try {
      await instance.loginRedirect({ scopes: ["openid", "profile", "email"] });
    } catch (err) {
      console.error('Login redirect failed:', err);
      setError(err?.message || 'Login failed.');
      setLoading(false);
    }
  }, [instance]);

  useEffect(() => {
    // Wait until MSAL is idle (including startup and handling the login redirect), so
    // the signed-in account is known before deciding between login page and dashboard.
    if (inProgress !== InteractionStatus.None) return;
    if (accounts && accounts.length > 0) {
      const activeAccount = instance.getActiveAccount();
      if (!activeAccount) instance.setActiveAccount(accounts[0]);
      setIsAuthenticated(true);
      acquireAccessToken();
    } else {
      setIsAuthenticated(false);
      setLoading(false);
    }
  }, [accounts, inProgress, acquireAccessToken, instance]);

  const handleLogout = async () => {
    await instance.logout();
    setIsAuthenticated(false);
    setAccessToken(null);
  };


  // LOADING STATE — also while a signed-in user's menu access is still being checked
  if (loading || (isAuthenticated && !menuAccessChecked && !error)) {
    return (
      <div className="app-container loading">
        <div className="spinner"></div>
        <p>Initializing...</p>
      </div>
    );
  }

  // ERROR STATE
  if (error) {
    return (
      <div className="app-container unauthenticated">
        <div className="login-container">
          <h1>H.I.S. NDC Aggregator Dashboard - Travelbrands GIGI</h1>
          <p style={{ color: '#dc2626' }}>Error: {error}</p>
          <button className="btn btn-primary" onClick={handleLogin}>Try Again</button>
        </div>
      </div>
    );
  }

  // NOT AUTHENTICATED
  if (!isAuthenticated) {
    return (
      <div className="app-container unauthenticated">
        <div className="login-container">
          <img src={require('./assets/HISLogo.png')} alt="HIS Logo" className="login-logo" />
          <h3>H.I.S. NDC Aggregator Dashboard <span className='block'>Travelbrands GIGI</span></h3>
          <p>Please sign in with your Microsoft Entra account to access the dashboard.</p>
          <button className="btn btn-primary btn-signin" onClick={handleLogin}>Sign In</button>
        </div>
      </div>
    );
  }

  // AUTHENTICATED WITH AZURE AD, BUT NOT REGISTERED FOR THE DASHBOARD
  if (notRegistered) {
    return (
      <div className="app-container unauthenticated">
        <div className="login-container">
          <img src={require('./assets/HISLogo.png')} alt="HIS Logo" className="login-logo" />
          <h3>H.I.S. NDC Aggregator Dashboard <span className='block'>Travelbrands GIGI</span></h3>
          <p style={{ color: '#dc2626' }}>Your Azure AD is not registered in our portal. Please contact administration.</p>
          <button className="btn btn-primary btn-signin" onClick={handleLogout}>Sign Out</button>
        </div>
      </div>
    );
  }

  // AUTHENTICATED APP
  return (
    <div className="app-container authenticated">

      {/* HEADER */}
     <header className="app-header ">

      {/* LEFT: Logo + Heading */}
      <div className="header-left d-flex align-items-center gap-2">
        <img
          src={require('./assets/images/his-logo.png')}
          alt="HIS Logo"
          className="header-logo"
          style={{ cursor: 'pointer' }}
          onClick={() => navigate(defaultPath || '/')}
        />
        <h3 className="mb-0">H.I.S. NDC Aggregator Dashboard - Travelbrands GIGI</h3>
      </div>

        {/* CENTER-RIGHT: Dashboard Menu */}
        <nav className={"dashboard-menu navigation gap-3 " + (isOpen ? "open" : "")} ref={navRef}>
          {navMenus.filter((m) => canAccess(m.menuKey)).map((m) => {
            const { path } = MENU_ROUTES[m.menuKey];
            return (
              <NavLink key={m.menuKey} to={path} className={`nav-link ${window.location.pathname === path ? 'active' : ''}`}>
                {m.label}
              </NavLink>
            );
          })}
        </nav>

        {/* RIGHT: User + Logout */}

        <Dropdown className="profile header__profile" 
            toggleContent={ 
              <>
                {/* <span className="icon"><CircleUserRound/></span> */}
                <span className="dropdown__avatar">
                  {(accounts.firstName?.[0] || accounts[0]?.name || "U").charAt(0).toUpperCase()} 
                </span>
              </>
              
              }
              items={[
                {label: accounts[0]?.name, iconBefore: <User size={16} />, isTextOnly: true },
                {label: 'Sign Out', iconBefore: <LogOut size={16} />, danger: true, onClick:handleLogout }
                
              ]}
            /> 

            <button className="nav-toggle header__nav--toggle md-hidden" onClick={() => setIsOpen(!isOpen)} >
              <svg className="pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>

        {/* <div className="user-section d-flex align-items-center gap-3">
          <div className="user-details">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-user-round-icon lucide-circle-user-round user-icon">
              <path d="M17.925 20.056a6 6 0 0 0-11.851.001"/>
              <circle cx="12" cy="11" r="4"/>
              <circle cx="12" cy="12" r="10"/>
            </svg>
            <span className="username">{accounts[0]?.name}</span>
          </div>
          <button className="btn-logout" onClick={handleLogout}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-log-out-icon lucide-log-out log-out-icon">
              <path d="m16 17 5-5-5-5"/>
              <path d="M21 12H9"/>
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
            </svg>
            Sign Out
          </button>
        </div> */}

    </header>

      {/* MAIN CONTENT */}
      <main className="app-main">
        <Routes>
          {Object.entries(MENU_ROUTES).map(([menuKey, { path, render }]) => (
            <Route key={menuKey} path={path} element={guard(menuKey, render(accessToken))} />
          ))}
          <Route path="*" element={<NotFound homePath={defaultPath} />} />
        </Routes>
      </main>
    </div>
  );
}

export default App;